import { formatDollars, formatDollarsAndCents, formatNumber } from '@makemydeal/dr-common-utils';
import { toSplitPercent } from '@makemydeal/dr-offer-redux';
import type { ProgramQuote, RetainedProgram, RetainedProgramDisplay } from '@makemydeal/dr-dash-types';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type {
    GenericObject,
    ILenderProgram,
    IResponsePayment,
    LenderDetailedAddress,
    OfferType,
    PaymentTerm
} from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, LEASE } from '@makemydeal/dr-platform-types';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import * as offerReduxSelectors from '../selectors/offerRedux';

const { isUserPricedProgram } = offerReduxUtils;

const LENDER_PROGRAM_ROW_KEYS = ['branchId', 'lenderId', 'programName', 'programType', 'regionalId'] as const;

type ProgramIdentity = {
    months?: number;
    lenderLegalName?: string;
    programName?: string;
};

const getEffectiveIdentity = (selectedTerm: PaymentTerm, retained: RetainedProgramDisplay | undefined): ProgramIdentity => {
    const { lenderLegalName: retainedLender, programName: retainedProgramName } = retained ?? {};
    const effectiveLender = selectedTerm.lenderLegalName || retainedLender;
    const lenderMatches = Boolean(retainedLender) && retainedLender === effectiveLender;
    const programName = selectedTerm.lenderProgram?.programName || (lenderMatches ? retainedProgramName : undefined);
    return {
        months: selectedTerm.months,
        lenderLegalName: effectiveLender,
        programName
    };
};

const RETAINED_PROGRAM_KEYS = [
    'lenderProgram',
    'lenderLegalName',
    'lenderCode',
    'lenderName',
    'branchName',
    'programLenderName',
    'creditTier',
    'creditTierDescription',
    'lenderAddress',
    'lenderPhoneNumber'
] as const;

export type RetainedProgramSource = {
    lenderProgram?: ILenderProgram;
    lenderLegalName?: string;
    lenderCode?: string;
    lenderName?: string;
    branchName?: string;
    programLenderName?: string;
    creditTier?: number;
    creditTierDescription?: string;
    creditTierDesc?: string;
    lenderAddress?: LenderDetailedAddress;
    lenderPhoneNumber?: string | null;
};

export const buildRetainedProgram = (source?: RetainedProgramSource): RetainedProgram | undefined => {
    if (!source?.lenderProgram?.programName) return undefined;
    const {
        lenderProgram,
        lenderLegalName,
        lenderCode,
        lenderName,
        branchName,
        programLenderName,
        creditTier,
        creditTierDescription,
        creditTierDesc,
        lenderAddress,
        lenderPhoneNumber
    } = source;
    return {
        lenderProgram,
        lenderLegalName,
        lenderCode,
        lenderName,
        branchName,
        programLenderName,
        creditTier,
        creditTierDescription: creditTierDescription ?? creditTierDesc,
        lenderAddress,
        lenderPhoneNumber
    };
};

const isSameRetainedProgram = (a: RetainedProgram, b: RetainedProgram): boolean =>
    RETAINED_PROGRAM_KEYS.every((key) =>
        key === 'lenderProgram' || key === 'lenderAddress' ? isEqual(a[key], b[key]) : a[key] === b[key]
    );

export const advanceRetainedProgram = (current?: RetainedProgram, incoming?: RetainedProgram): RetainedProgram | undefined => {
    if (!incoming?.lenderProgram?.programName) return current;
    if (isUserPricedProgram(incoming.lenderProgram)) return current;
    if (current && isSameRetainedProgram(current, incoming)) return current;
    return incoming;
};

export const describesSameLenderProgram = (a?: GenericObject, b?: GenericObject): boolean => {
    if (!a?.programName || !b?.programName) return false;
    return LENDER_PROGRAM_ROW_KEYS.every((key) => a[key] === b[key]);
};

export const buildIncentivesContextId = (program?: ProgramQuote): string | undefined => {
    if (!program) return undefined;

    const lenderProgram = program.lenderProgram;
    if (!lenderProgram && program.term == null) return undefined;

    return [program.term ?? '', ...LENDER_PROGRAM_ROW_KEYS.map((k) => lenderProgram?.[k] ?? '')].join('|');
};

export const buildProgramContextId = (program?: ProgramQuote): string | undefined => {
    if (!program) return undefined;

    const incentivesContextId = buildIncentivesContextId(program);
    if (incentivesContextId == null) return undefined;

    return [incentivesContextId, program.creditTier ?? ''].join('|');
};

export const buildProgramContextIdFromTerm = (term?: PaymentTerm): string | undefined =>
    buildProgramContextId({
        term: term?.months,
        lenderProgram: term?.lenderProgram,
        creditTier: term?.creditTier
    } as ProgramQuote);

const matchesProgramIdentity = (identity: ProgramIdentity, currentProgram: ProgramQuote): boolean => {
    const selectedProgramName = identity.programName || undefined;
    const currentProgramName = currentProgram.lenderProgram?.programName || undefined;
    return (
        identity.months != null &&
        identity.months === currentProgram.term &&
        Boolean(identity.lenderLegalName) &&
        identity.lenderLegalName === currentProgram.lenderLegalName &&
        selectedProgramName === currentProgramName
    );
};

const RATE_MATCH_EPSILON = 1e-6;
const parseRate = (rate?: string | number): number => (typeof rate === 'number' ? rate : parseFloat(rate ?? '0'));
const ratesEqual = (a?: string | number, b?: string | number): boolean =>
    Math.abs(parseRate(a) - parseRate(b)) < RATE_MATCH_EPSILON;

const matchesRate = (selectedTerm: PaymentTerm, currentProgram: ProgramQuote): boolean =>
    ratesEqual(selectedTerm.buyRate, currentProgram.buyRate);

const offerTypeRequiresRateMatch = (offerType: OfferType): boolean => offerType === LEASE || offerType === BALLOON;

const matchesQuoteRate = (a: ProgramQuote, b: ProgramQuote): boolean => ratesEqual(a.buyRate, b.buyRate);

export const isSameProgramRow = (a: ProgramQuote, b: ProgramQuote, offerType: OfferType): boolean => {
    const idA = buildProgramContextId(a);
    if (idA == null || idA !== buildProgramContextId(b)) return false;
    return !offerTypeRequiresRateMatch(offerType) || matchesQuoteRate(a, b);
};

export const matchesProgramIdentityForTerm = (
    selectedTerm: PaymentTerm,
    currentProgram: ProgramQuote,
    retained?: RetainedProgramDisplay
): boolean => matchesProgramIdentity(getEffectiveIdentity(selectedTerm, retained), currentProgram);

export const isSelectedProgram = (
    selectedTerm: PaymentTerm,
    currentProgram: ProgramQuote,
    offerType: OfferType,
    retained?: RetainedProgramDisplay
): boolean => {
    if (!matchesProgramIdentityForTerm(selectedTerm, currentProgram, retained)) return false;
    return !offerTypeRequiresRateMatch(offerType) || matchesRate(selectedTerm, currentProgram);
};

export const isAppliedRowDisplay = (
    selectedTerm: PaymentTerm,
    currentProgram: ProgramQuote,
    offerType: OfferType,
    appliedProgram?: ProgramQuote,
    retained?: RetainedProgramDisplay
): boolean => {
    if (offerType === CASH) return true;
    if (!isSelectedProgram(selectedTerm, currentProgram, offerType, retained)) return false;
    const appliedTier = appliedProgram?.creditTier;
    const currentTier = currentProgram.creditTier;
    if (appliedTier != null && currentTier != null) return appliedTier === currentTier;
    return true;
};

export const isProgramNotExpired = (quote: ProgramQuote): boolean => {
    const endDate = (quote.lenderProgram as { programEndDate?: string } | undefined)?.programEndDate;
    if (!endDate) return true;

    const isExpired = DateTime.fromISO(endDate).diff(DateTime.now(), 'days').days <= -8;
    return !isExpired;
};

export const mapPaymentResponseToProgramQuote = (quote: IResponsePayment, useProgramResidual = false): ProgramQuote => {
    const {
        appliedIncentiveAmount,
        appliedIncentiveIds,
        baseResidualDollar,
        baseResidualPercent,
        buyRate,
        creditTier,
        creditTierDescription,
        lenderCode,
        lenderLegalName,
        lenderProgram,
        monthlyPayment,
        profit,
        programResidual,
        programResidualPercent,
        rateType,
        securityDeposit,
        sellRate,
        term,
        annualMiles,
        balloonType
    } = quote;

    const { maxResidualizedMsrp, maxResidualizedMsrpApplied } = quote as IResponsePayment & {
        maxResidualizedMsrp?: number;
        maxResidualizedMsrpApplied?: boolean;
    };

    const acqFee = offerReduxSelectors.getDealerAcquisitionFeeFromTerm(quote as any)?.dealerFeeAmount;
    const reserveSplitPercent = toSplitPercent(profit?.reservePaid);

    const formattedData = {
        acqFeeFormatted: acqFee == null ? undefined : formatDollars(acqFee),
        acqFee,
        appliedIncentiveAmount: formatDollarsAndCents(appliedIncentiveAmount),
        appliedIncentiveIds,
        backEndProfit: formatDollarsAndCents(profit?.backEndProfit),
        baseResidualDollar: formatDollarsAndCents(useProgramResidual ? programResidual : baseResidualDollar),
        balloonAmount: formatDollarsAndCents(baseResidualDollar),
        balloonTermType: balloonType === 'term' ? 'Term' : 'Term-1',
        baseResidualPercent: useProgramResidual ? programResidualPercent : baseResidualPercent,
        buyRate: formatRate(buyRate, rateType),
        creditScore: (quote as IResponsePayment & { creditScore?: number }).creditScore,
        creditTier,
        creditTierDescription,
        requestedTier: (quote as IResponsePayment & { requestedTier?: string }).requestedTier,
        frontEndProfit: formatDollarsAndCents(profit?.frontEndProfit),
        lenderCode,
        lenderLegalName,
        lenderProgram,
        monthlyPayment: formatDollarsAndCents(monthlyPayment),
        rateType,
        securityDeposit,
        securityDepositFormatted: formatDollarsAndCents(securityDeposit),
        sellRate: formatRate(sellRate, rateType),
        term,
        annualMiles,
        annualMilesFormatted: formatNumber(annualMiles || 0),
        totalProfit: formatDollarsAndCents(profit?.totalProfit),
        estReserveGross: profit?.reserveFromInterestParticipation ?? undefined,
        reserveSplit: reserveSplitPercent == null ? '' : `${reserveSplitPercent}%`,
        reserveProfit: formatDollarsAndCents(profit?.reserveProfit),
        profit,
        maxResidualizedMsrp,
        maxResidualizedMsrpApplied
    };

    return formattedData;
};

export const formatRate = (rate: number, rateType: string) => {
    if (rateType === 'APR') return formatAprRate(rate);
    return formatMoneyFactor(rate);
};

export const formatAprRate = (amount: number): string => {
    return numeral(amount).format('0,0.000');
};

export const formatMoneyFactor = (amount: number): string => {
    return numeral(amount).format('0,0.00000');
};
